/**
 * worldProgress.ts
 * Pure read-only helpers that summarise SaveData for UI screens.
 *
 * Belongs here (not in `app/`) because AGENTS.md requires `app/` files
 * to be thin Expo Router wrappers with no gameplay/data logic.
 *
 * @module worldProgress
 */

import type { SaveData } from '../types/save';

/**
 * Total Constellation Shards available per world.
 * Per ROADMAP Phase 5: 12 shards per world (3 per main level × 4 levels).
 */
export const SHARDS_PER_WORLD = 12;

/**
 * Total **main** levels per world. Secret stages (`X-0`), bonus stages
 * (`X-S`), and boss stages (`X-B`) are not counted toward this total —
 * the world-map UI uses this as the denominator for "stages cleared".
 */
export const MAIN_LEVELS_PER_WORLD = 4;

/**
 * Canonical positive-integer pattern for main-level suffixes: one or more
 * digits with no leading zero. Rejects `Number(...)` aliases like `"01"`,
 * `"1.0"`, `"1e0"`, `"+1"`, and `" 1"` that would otherwise let a
 * malformed save register the same stage twice (e.g. `"1-1"` and
 * `"1-01"` both parsing to `1`).
 */
const MAIN_STAGE_SUFFIX_PATTERN = /^[1-9]\d*$/;

/**
 * Parse a level-key suffix as a canonical main-stage number, or return
 * `null` if the suffix is non-canonical, out of range, or non-numeric.
 *
 * Used by both shard- and completion-counters to enforce a strict
 * `[1, MAIN_LEVELS_PER_WORLD]` bound so UI screens cannot render values
 * above their denominators.
 *
 * @param suffix - Substring after the `"${worldId}-"` prefix.
 * @returns Main-stage number in `[1, MAIN_LEVELS_PER_WORLD]`, or `null`.
 */
// DECISION: Regex-then-Number over Number-then-roundtrip — the roundtrip
// `String(Number(s)) === s` is correct here but obscures intent; the
// pattern documents the accepted form (`"1"`..`"4"`) at the call site.
function parseMainStageNumber(suffix: string): number | null {
  if (!MAIN_STAGE_SUFFIX_PATTERN.test(suffix)) return null;
  const n = Number(suffix);
  if (n < 1 || n > MAIN_LEVELS_PER_WORLD) return null;
  return n;
}

/**
 * Sum `collectedConstellationShardIds.length` across every **main** level
 * entry whose key matches `"${worldId}-N"` for an integer N in
 * `[1, MAIN_LEVELS_PER_WORLD]`.
 *
 * Secret stages (`X-0`), bonus stages (`X-S`), and boss stages (`X-B`) are
 * excluded because `SHARDS_PER_WORLD` is the main-level total (12 = 3×4).
 * Stale or future keys above {@link MAIN_LEVELS_PER_WORLD} (e.g. `X-5`
 * from a hand-edited save or a future content drop) are also excluded so
 * the UI cannot render values > `SHARDS_PER_WORLD`. Non-canonical numeric
 * aliases (`"01"`, `"1.0"`, `"1e0"`) are rejected by
 * {@link parseMainStageNumber} so a malformed save cannot double-count
 * the same logical stage.
 *
 * @param save    - SaveData to read, or null for a missing/corrupt save.
 * @param worldId - World id (1..5).
 * @returns Total main-level shards collected in `worldId` (0 when save is null).
 */
export function countCollectedShards(save: SaveData | null, worldId: number): number {
  if (save == null) return 0;
  const prefix = `${worldId}-`;
  let total = 0;
  for (const [key, progress] of Object.entries(save.levels)) {
    if (!key.startsWith(prefix)) continue;
    if (parseMainStageNumber(key.slice(prefix.length)) === null) continue;
    total += progress.collectedConstellationShardIds.length;
  }
  return total;
}

/**
 * Count completed **main** level entries (`"${worldId}-N"` for integer N in
 * `[1, MAIN_LEVELS_PER_WORLD]`) for `worldId`. Mirrors
 * {@link countCollectedShards} for the same set of keys, but reads
 * `progress.completed` instead of summing shard ids.
 *
 * Secret, bonus, and boss stages are excluded; stale or future keys above
 * {@link MAIN_LEVELS_PER_WORLD} (e.g. a hand-edited save with `X-5`) are
 * also excluded so the result is bounded by `MAIN_LEVELS_PER_WORLD` — the
 * world-map UI uses this as the numerator in an "X / 4 stages cleared"
 * line and must never render `5 / 4`. Non-canonical numeric aliases
 * (`"01"`, `"1.0"`, `"1e0"`) are rejected by {@link parseMainStageNumber}.
 *
 * @param save    - SaveData to read, or null for a missing/corrupt save.
 * @param worldId - World id (1..5).
 * @returns Completed main-level count for `worldId` (0 when save is null).
 */
export function countCompletedMainLevels(
  save: SaveData | null,
  worldId: number,
): number {
  if (save == null) return 0;
  const prefix = `${worldId}-`;
  let total = 0;
  for (const [key, progress] of Object.entries(save.levels)) {
    if (!key.startsWith(prefix)) continue;
    if (parseMainStageNumber(key.slice(prefix.length)) === null) continue;
    if (progress.completed) total += 1;
  }
  return total;
}
